/**
 * What an event means, now that it does not mean "fetch everything again".
 *
 * # The cost this replaces
 *
 * The daemon's push stream polls one shared change counter every two seconds
 * (`POLL` in `crates/hm-daemon/src/push.rs`) and emits a `StateChange` whenever
 * it moves. During a backfill it moves on every batch. The client's answer to
 * any event used to be `store.refresh({ quiet: true })` -- the session
 * document, every mailbox in every account, and then a full re-query of the
 * message list to the depth the reader had scrolled. Measured against the live
 * server that is about 139 KB every two seconds, roughly 250 MB an hour, to
 * learn that some 2019 mail had been imported into an archive nobody was
 * looking at.
 *
 * The event carries no information beyond "something moved", so the fix cannot
 * be to read it more carefully. It has to be to ask a question whose answer is
 * small.
 *
 * # The question
 *
 * `Email/queryChanges` and `Mailbox/changes`, in one request, both since the
 * state the client last actually saw. Then:
 *
 *   - Nothing changed that is in the current view: nothing is refetched. This
 *     is the case a backfill produces over and over, and it is the whole point.
 *   - Folder counters moved: the changed folders are refetched and no more.
 *     Four integers per folder is the cheap call the tab counts and the sidebar
 *     badges need, and it is a different order of cost from the message list.
 *   - Something in the view changed: the list is reloaded exactly as before.
 *
 * `Email/queryChanges` rather than `Email/changes` is the load-bearing choice.
 * `Email/changes` answers with ids, and an id says nothing about whether the
 * message is in the view -- deciding that means knowing its mailboxes and its
 * keywords, so a client built on it would have to fetch every changed message
 * to discover that none of them mattered. During a backfill that is the fetch
 * being avoided, so it would avoid nothing. `queryChanges` asks the server,
 * which has the filter compiled already, and gets a decision back:
 * `added` is the messages that now match the query, and `removed` is every
 * message that changed at all. See `crates/hm-jmap/src/changes.rs`, which says
 * so in its own words -- it puts every changed message in `removed` and then
 * re-inserts the ones that still match, because a message that merely moved is
 * a removal plus a re-insertion in JMAP's model.
 *
 * # `cannotCalculateChanges` is a normal answer
 *
 * The change log is pruned (`CHANGE_LOG_LIMIT` in `crates/hm-sync`), so a
 * client whose state predates the floor cannot be told what it missed. The
 * daemon says so rather than inventing a partial delta, and the only correct
 * response is the full reload this module exists to avoid -- which is fine,
 * because it happens after a long sleep rather than every two seconds. A tab
 * left open over a weekend takes that path once and then goes back to deltas.
 *
 * # Why the debounce is here and not in `lib/events.ts`
 *
 * `events.ts` already refuses to overlap two refreshes and remembers at most
 * one. That collapses a burst that arrives *during* the work; it does nothing
 * about a burst that arrives while the client is idle, which is four events in
 * eight seconds during a sync and four rounds of work. A short settle window
 * turns that into one. It is deliberately short: a genuine new message is the
 * one event nobody may be made to wait for, and a quarter of a second is under
 * the threshold at which a person notices a list updating.
 */

import { replicate } from "@transport";

import * as jmap from "./jmap";
import * as store from "./store";

/**
 * How long a burst of events is allowed to settle into one round of work.
 *
 * Read the note above before lengthening it. This is added to the delay before
 * a message someone has just been sent appears on screen.
 */
const SETTLE = 250;

let settling: Promise<void> | null = null;

/**
 * Bring the client up to date, coalescing a burst of events into one round.
 *
 * The returned promise resolves when the work for this event is done, so the
 * caller in `lib/events.ts` can go on serialising: at most one of these runs at
 * a time, and at most one more is remembered.
 */
export function sync(): Promise<void> {
  if (settling) return settling;
  const wait = new Promise<void>((resolve) => setTimeout(resolve, SETTLE));
  settling = wait.then(() => {
    // Cleared before the work rather than after it, so an event that arrives
    // while the request is in flight starts a fresh window rather than being
    // answered by a round that had already read the state it was about.
    settling = null;
    return run().finally(() => {
      // Housekeeping, after the view is right rather than before it.
      //
      // Not awaited, and in a `finally` so it still happens when the delta
      // failed. A dead session is exactly when the local copy is most worth
      // having, and it is also when this round is most likely to have thrown.
      replicateAll();
      // The reminders list is server state the stream cannot describe as a
      // delta; one read per settled burst keeps every device's section and
      // chip in step, and turns a reminder that just fired into an overdue
      // row here at the moment the phone buzzes.
      void store.refreshReminders();
    });
  });
  return settling;
}

/**
 * Bring this device's own copy of the mail up to date, and let it say so.
 *
 * On a build with a local replica this runs a replication pass; in a browser it
 * does nothing at all, and this function does not know which it is —
 * `lib/net.types.ts` explains why that is the arrangement.
 *
 * # Why this is named, and not left inline in `sync`
 *
 * Because it is the only thing *this page* can do that produces a notification,
 * and for a long time `sync` was its only caller — which quietly made *the
 * stream* the only thing that could produce one. A replication pass is what
 * reaches `announce` in `core/src/net.rs`; a `store.refresh` is not, however
 * much fresh mail it puts on screen. The two look interchangeable from here and
 * are not, so the one that notifies has a name and the backstop in `app.tsx`
 * calls it by that name. (The shell can run the same pass without us — it does
 * so exactly when this page's timers are suspended, a hidden window on macOS —
 * see `src-tauri/src/background.rs`; the two drivers share one step lock.)
 *
 * Deliberately not awaited by its callers and deliberately returning nothing:
 * a walk of the archive must never be something the interface waits for.
 */
export function replicateAll(): void {
  replicate(store.accounts.peek().map((a) => a.id));
}

/** Forget any pending settle window. For tests, and for signing out. */
export function reset(): void {
  settling = null;
}

async function run(): Promise<void> {
  // No session yet: this is the first thing to happen after the daemon came
  // back, and there is nothing to take a delta against.
  if (!store.session.peek()) {
    await store.connect();
    return;
  }

  const listAccounts = store.scopeAccounts.peek();
  // Every account, not just the ones in the view. The sidebar draws a counter
  // for mailboxes nobody is looking at, and those are exactly the ones a
  // backfill is moving.
  const folderAccounts = store.accounts.peek().map((a) => a.id);

  const queries: jmap.QueryProbe[] = [];
  let listNeedsFullReload = false;
  for (const accountId of listAccounts) {
    const since = store.listStateOf(accountId);
    if (!since) {
      // No state means the last read of this account's list failed or never
      // happened. Asking for changes since a state we never had is the one
      // thing that would silently lose mail, so this is the case that goes the
      // long way round.
      listNeedsFullReload = true;
      break;
    }
    queries.push({
      spec: store.currentSpec(accountId),
      sinceQueryState: since,
      upToId: store.oldestHeld(accountId),
    });
  }

  const folders = folderAccounts
    .map((accountId) => ({ accountId, sinceState: store.folderStateOf(accountId) }))
    .filter((p): p is { accountId: string; sinceState: string } => Boolean(p.sinceState));
  const foldersNeedFullReload = folders.length !== folderAccounts.length;

  if (listNeedsFullReload && foldersNeedFullReload) {
    await store.refresh({ quiet: true });
    return;
  }

  let deltas;
  try {
    deltas = await jmap.pollChanges(listNeedsFullReload ? [] : queries, folders);
  } catch {
    // The request itself failed -- offline, a 502, a daemon restarting. Not a
    // reason to reload the world; the stream will wake us again, and the
    // five-minute backstop in `app.tsx` is what catches a client that has
    // stopped hearing anything at all.
    return;
  }

  const held = store.heldKeys();
  let reloadList = listNeedsFullReload;
  const listCaughtUpTo = new Map<string, string>();
  const refetch = new Map<string, string[]>();

  for (const delta of deltas.queries) {
    if (delta.error) {
      // `cannotCalculateChanges` is the expected one and the fallback is the
      // full reload. Anything else is a refusal this client did not anticipate,
      // and the safe reading of "I cannot tell you what changed" is the same.
      reloadList = true;
      continue;
    }
    // Something the client has not got now matches the query. A new arrival,
    // or a message moved into view from somewhere else: only a requery can put
    // it in its right place among rows this client has never seen.
    //
    // Held ids in `added` are a different animal, and the distinction is the
    // point of this loop. The daemon reports every changed message as removed
    // and re-adds the ones that still match (see `changes.rs`), so the echo of
    // this client's own "mark as read" arrives as `removed: [id], added: [id]`
    // -- and treating any `added` as "reload the world" made every click
    // repaint the whole list a second later. A held id is a row already on
    // screen in its right place: the sort key is `receivedAt`, which never
    // changes, so a held message cannot have moved relative to the other rows
    // unless something *unheld* joined -- which is exactly the case the line
    // above catches.
    if (delta.added.some((id) => !held.has(`${delta.accountId}:${id}`))) {
      reloadList = true;
      continue;
    }
    // What remains is changes to messages this client is holding -- its own
    // edits coming back round, or another device touching a row on screen.
    // Those are refetched by id and patched in place rather than paid for with
    // a rebuild of the list. `added` is a subset of `removed` by construction,
    // so filtering `removed` catches both the still-matching and the departed.
    const changed = delta.removed.filter((id) => held.has(`${delta.accountId}:${id}`));
    if (changed.length > 0) refetch.set(delta.accountId, changed);
    if (delta.newQueryState) listCaughtUpTo.set(delta.accountId, delta.newQueryState);
  }

  let reloadFolders = foldersNeedFullReload;
  const folderWork: { accountId: string; list: jmap.MailboxDelta["list"]; state: string }[] = [];

  for (const delta of deltas.mailboxes) {
    if (delta.error || delta.hasMoreChanges) {
      reloadFolders = true;
      continue;
    }
    // A folder appearing or disappearing is not a counter moving. It is a new
    // account provisioned from the CLI, a folder created upstream, or a mailbox
    // gone -- and the first of those is invisible to every delta this client
    // can ask for, because it does not know the account exists to ask about it.
    // The session document is the only thing that carries that news.
    if (delta.created.length > 0 || delta.destroyed.length > 0) {
      reloadFolders = true;
      continue;
    }
    if (delta.newState) {
      folderWork.push({ accountId: delta.accountId, list: delta.list, state: delta.newState });
    }
  }

  if (reloadFolders) {
    // `refreshSession` re-reads the session and every mailbox, and records the
    // states as it goes. It is the cheap half of what an event used to cost.
    await store.refreshSession();
  } else {
    for (const work of folderWork) {
      store.patchFolders(work.accountId, work.list);
      store.noteFolderState(work.accountId, work.state);
    }
  }

  if (reloadList) {
    // Quiet: the rows on screen stay put while the new ones are fetched, the
    // reprieved rows keep their reprieve, and the refetch goes to the depth the
    // reader has scrolled rather than snapping back to the first page.
    await store.reload({ quiet: true });
    return;
  }

  // The held-only path: fresh copies of the changed rows, absorbed in place.
  // One `Email/get` per touched account instead of a requery of everything,
  // and -- the part someone can see -- the rows that did not change are not
  // rebuilt. `store.absorb` decides per message whether the row stays, goes,
  // or keeps its reprieve, by the same rules a reload would have applied.
  const current = store.session.peek();
  for (const [accountId, ids] of refetch) {
    // Signed out between the poll and now. Advancing the state for an account
    // whose rows were never brought up to date would skip these changes for
    // good, so it is dropped along with the work.
    if (!current) {
      listCaughtUpTo.delete(accountId);
      continue;
    }
    let fetched;
    try {
      fetched = await jmap.getEmails(current, accountId, ids);
    } catch {
      // The refetch failed after the poll succeeded -- a daemon mid-restart,
      // usually. The state deliberately does not advance for this account, so
      // the next event asks about the same changes instead of skipping past
      // them; the rows stay as they are until it does.
      listCaughtUpTo.delete(accountId);
      continue;
    }
    const found = new Set(fetched.map((e) => e.id));
    store.absorb(
      accountId,
      fetched,
      ids.filter((id) => !found.has(id)),
    );
  }

  // The states advance -- that is what stops the next event asking about the
  // same changes, and what keeps the client's state inside the log's retention
  // floor.
  for (const [accountId, state] of listCaughtUpTo) store.noteListState(accountId, state);
}
